import { debounce } from 'lodash';
import sentryUtils from './3rd-party/sentry';

interface Log {
  type: string;
  values: any;
}

/**
 * Creates a batch logger that collects logs and sends them in batches to a specified API endpoint.
 *
 * @param apiCall - A function that takes an array of logs and returns a Promise that resolves when the logs have been successfully sent.
 * @param batchInterval - The interval in milliseconds at which to send batches of logs. Defaults to 80 milliseconds.
 * @param maxBatchSize - The maximum number of logs to include in each batch. Defaults to 200 logs.
 * @returns A function that adds a log to the batch and schedules it to be sent.
 */
const createBatchLogger = (apiCall: (logs: Log[]) => Promise<void>, batchInterval: number = 80, maxBatchSize: number = 200) => {
  const logBatch: Log[] = [];

  const sendBatch = async () => {
    if (logBatch.length === 0) return;

    const batchToSend = logBatch.splice(0, maxBatchSize);

    try {
      await apiCall(batchToSend);
    } catch (error) {
      sentryUtils.captureError(error as any);
    }
  };

  const debouncedSendBatch = debounce(sendBatch, batchInterval);

  const addLog = (log: Log) => {
    logBatch.push(log);
    debouncedSendBatch();
  };

  return addLog;
};

export default createBatchLogger;
